import { pick } from 'lodash';
import { addDays, subDays } from 'date-fns';

import { ScriptLoader } from './scriptLoader';
import { getStartDateByPreset } from '../pages/hotelWidget/utils/index';

import { WidgetName } from '../models';

import type { SearchType } from '@lt/api/dist/types/searchParams';
import type { Values } from '../components/Cigarette/context';
import type { CustomShowParams } from '../context/constructorContext/hooks/useTracker';

const TRACKER_URL = 'https://cdn.level.travel/tracker/tracker.js';
const TRACKER_ID = '727272';

const widgetNameToSnakeCase = {
  [WidgetName.CIGARETTE]: 'cigarette_widget_v2',
  [WidgetName.DIRECTION]: 'direction_widget_v2',
  [WidgetName.HOTEL]: 'hotel_widget_v2',
  [WidgetName.METAREGIONS]: 'metaregions_widget_v2',
  [WidgetName.PACKAGE]: 'package_widget_v2',
  [WidgetName.SEARCH]: 'search_widget_v2',
};

export const getWidgetNameInSnakeCase = (
  widgetName: WidgetName,
  searchType?: SearchType
): string => {
  if (widgetName === WidgetName.CIGARETTE) {
    return `search_${searchType}_widget_v2`;
  }

  return widgetNameToSnakeCase[widgetName];
};

export const convertSearchParamsForTracking = (searchParams: Record<string, any>) => {
  if (!searchParams || !Object.keys(searchParams).length) {
    return;
  }

  const startDate = getStartDateByPreset(searchParams.start_date);
  const nightsRange = searchParams.nights
    ? typeof searchParams.nights === 'string'
      ? searchParams.nights.split('..').map((nightsCount: string) => Number(nightsCount))
      : [searchParams.nights - 1, searchParams.nights + 1]
    : undefined;

  const searchParamsForTracking = {
    ...pick(searchParams, ['adults', 'kids', 'search_type', 'hotel_id']),
    ...(searchParams.kids && { kids_ages: new Array(searchParams.kids).fill(7) }),
    ...(searchParams.departure && {
      departure_id: searchParams.departure.id,
    }),
    ...(searchParams.destination_ids &&
      (Array.isArray(searchParams.destination_ids)
        ? { destination_ids: searchParams.destination_ids }
        : { destination_id: searchParams.destination_ids.value })),
    ...(startDate &&
      nightsRange && {
        start_date_from: startDate,
        start_date_to: startDate,
        end_date_from: addDays(startDate, nightsRange[0]),
        end_date_to: addDays(startDate, nightsRange[1]),
      }),
  };

  return searchParamsForTracking;
};

export const convertCigaretteParamsForTracking = (cigaretteParams: Partial<Values>) => {
  const datePickerStartDate = cigaretteParams.datepicker?.left;
  const start_date_from =
    cigaretteParams.datepicker &&
    datePickerStartDate?.date &&
    subDays(datePickerStartDate.date, cigaretteParams.datepicker.shift);
  const start_date_to =
    cigaretteParams.datepicker &&
    datePickerStartDate?.date &&
    addDays(datePickerStartDate.date, cigaretteParams.datepicker.shift);

  const datePickerEndDate = cigaretteParams.datepicker?.right;
  const end_date_from =
    cigaretteParams.datepicker &&
    datePickerEndDate?.date &&
    subDays(datePickerEndDate.date, cigaretteParams.datepicker.shift);
  const end_date_to =
    cigaretteParams.datepicker &&
    datePickerEndDate?.date &&
    addDays(datePickerEndDate.date, cigaretteParams.datepicker.shift);

  const cigaretteParamsForTracking = {
    ...(cigaretteParams.destination && { destination_id: cigaretteParams.destination.value }),
    ...(cigaretteParams.departure && { departure_id: cigaretteParams.departure.id }),
    ...(start_date_from && { start_date_from }),
    ...(start_date_from && { start_date_to }),
    ...(end_date_from && { end_date_from }),
    ...(end_date_to && { end_date_to }),
    ...pick(cigaretteParams.tourists, 'adults'),
    ...(cigaretteParams.tourists?.kids && { kids: cigaretteParams.tourists.kids.length }),
    ...(cigaretteParams.tourists?.kids?.length && { kids_ages: cigaretteParams.tourists.kids }),
  };

  return cigaretteParamsForTracking;
};

export const loadTracker = async () => {
  if (window['trackerLoadingStarted']) {
    setTimeout(() => {
      if (!window['trackerWidget']) {
        console.log('Не удалось загрузить hit storage tracker');
      }
    }, 1000);

    return;
  }

  window['trackerLoadingStarted'] = true;

  const loader = new ScriptLoader({
    src: TRACKER_URL,
    global: 'HitStorageTracker',
  });

  return loader.load();
};

export const initTracker = async (): Promise<void> => {
  try {
    if (window['trackerWidget']) {
      return;
    }

    const HitStorageTracker = await loadTracker();
    if (!HitStorageTracker) {
      return;
    }

    const instance = new HitStorageTracker(TRACKER_ID);
    window['trackerWidget'] = instance;
  } catch (error) {
    console.log('Невозможно загрузить hit storage tracker', error);
  }
};

type BaseTrackParams = {
  widgetName: string;
  partnerName: string;
};

type TrackWidgetShowParams = BaseTrackParams & {
  customShowParams?: Partial<CustomShowParams>;
  searchParams?: any;
};

type TrackWidgetSearchParamsChange = BaseTrackParams & {
  changedParams: Partial<Values>;
  searchParams: any;
};

export type WidgetErrorType = 'error_render' | 'error_data';

type TrackWidgetError = BaseTrackParams & {
  errorType: WidgetErrorType;
};

export const trackWidgetShow = ({
  widgetName,
  partnerName,
  customShowParams = {},
  searchParams,
}: TrackWidgetShowParams): void => {
  const { search_params, ...otherParams } = customShowParams;

  const params = {
    affiliate: partnerName,
    search_params: search_params || searchParams,
    custom_params: {
      ...otherParams,
    },
  };

  window['trackerWidget']?.add('pageview', widgetName, params);
};

export const trackWidgetSearchParamsChange = ({
  widgetName,
  partnerName,
  searchParams,
  changedParams,
}: TrackWidgetSearchParamsChange): void => {
  const changes = convertCigaretteParamsForTracking(changedParams);

  const params = {
    affiliate: partnerName,
    search_params: { ...searchParams, ...changes },
    custom_params: {
      changes,
    },
  };

  window['trackerWidget']?.add('search_params_change', widgetName, params);
};

export const trackWidgetError = ({
  widgetName,
  partnerName,
  errorType,
}: TrackWidgetError): void => {
  const params = {
    affiliate: partnerName,
    custom_params: {
      error_type: errorType,
    },
  };

  window['trackerWidget']?.add('error', widgetName, params);
};
